import { createFileRoute, useRouter } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { GlossyInput } from '@/components/auth/GlossyInput'
import { FormErrorMessage } from '@/components/auth/FormErrorMessage'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useEffect, useCallback } from 'react'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useLogin, useGoogleAuthCallback, useAppleAuthCallback } from '@/hooks/api/useAuth'
import { signInWithGoogle, signInWithApple, getOAuthRedirectResult } from '@/auth/firebase'
import GoogleIcon from '@/icons/google'
import AppleIcon from '@/icons/apple'
import { useOnboardingStore } from '@/stores/onboarding'
import { captureException } from '@/config/sentry'
import { isWebPlatform } from '@/utils/platformDetection'

type LoginSearchParams = {
    returnToPath?: string
}

export const Route = createFileRoute('/(onboarding)/(auth)/login')({
    validateSearch: (search: Record<string, unknown>): LoginSearchParams => {
        return {
            returnToPath:
                typeof search.returnToPath === 'string'
                    ? search.returnToPath
                    : undefined,
        }
    },
    component: LoginRoute,
})

function LoginRoute() {
    const router = useRouter()
    const { returnToPath } = Route.useSearch()
    const {
        mutate: login,
        isPending: isLoginLoading,
        error: _loginApiError,
        reset: resetLogin,
    } = useLogin()
    const {
        mutateAsync: googleAuthCallback,
        isPending: isGoogleCallbackLoading,
        error: _googleCallbackApiError,
        reset: resetGoogleCallback,
    } = useGoogleAuthCallback()

    const {
        mutateAsync: appleAuthCallback,
        isPending: isAppleCallbackLoading,
        error: _appleCallbackApiError,
        reset: resetAppleCallback,
    } = useAppleAuthCallback()

    const { setRegistrationData } = useOnboardingStore()

    const [isGoogleSignInLoading, setIsGoogleSignInLoading] = useState(false)
    const [isAppleSignInLoading, setIsAppleSignInLoading] = useState(false)
    const isLoading = isLoginLoading || isGoogleCallbackLoading || isGoogleSignInLoading || isAppleCallbackLoading || isAppleSignInLoading

    const [displayError, setDisplayError] = useState<string | null>(null)

    const getErrorMessage = useCallback((error: Error | null): string | null => {
        if (!error) return null

        if (error.message?.includes('auth/user-not-found')) {
            return 'No account found with this email address'
        }
        if (
            error.message?.includes('auth/wrong-password') ||
            error.message?.includes('auth/invalid-credential')
        ) {
            return 'Incorrect email or password'
        }
        if (error.message?.includes('auth/too-many-requests')) {
            return 'Too many failed login attempts. Please try again later'
        }
        if (error.message?.includes('Invalid ID token')) {
            return 'Google authentication failed. Please try again.'
        }

        // For all other errors, return the actual error message
        // This ensures specific errors (like simulator warnings) are shown to users
        if (error instanceof Error && error.message) {
            return error.message
        }
        return 'An unexpected error occurred. Please try again.'
    }, [])

    const handleSuccessfulAuth = useCallback(
        async (idToken: string) => {
            // Check for auth flow marker from withAuth
            const markerStr = sessionStorage.getItem('authFlowMarker')

            if (markerStr) {
                try {
                    const marker = JSON.parse(markerStr) as {
                        startPath: string
                        historyLength: number
                        timestamp: number
                    }

                    // Calculate how many steps back to go
                    const currentLength = window.history.length
                    const stepsToGoBack = currentLength - marker.historyLength

                    if (stepsToGoBack > 0) {
                        // Go back to the original page, clearing all auth pages
                        // Clean up marker AFTER navigation completes
                        window.history.go(-stepsToGoBack)
                        // Note: marker will be cleaned up by the target page on mount
                        return
                    } else {
                        // Fallback: navigate with replace if something went wrong
                        sessionStorage.removeItem('authFlowMarker')
                        router.navigate({ to: marker.startPath, replace: true })
                        return
                    }
                } catch (e) {
                    console.error('Failed to parse auth flow marker:', e)
                    sessionStorage.removeItem('authFlowMarker')
                }
            }

            // Check if this is an extension authentication request
            const urlParams = new URLSearchParams(window.location.search)
            const source = urlParams.get('source')

            if (
                source === 'extension' &&
                returnToPath?.includes('extension-auth-callback')
            ) {
                // Redirect to the extension callback with the token
                const callbackUrl = new URL(returnToPath, window.location.origin)
                callbackUrl.searchParams.set('token', idToken)
                callbackUrl.searchParams.set('source', 'extension')

                // Preserve state parameter if present
                const state = urlParams.get('state')
                if (state) {
                    callbackUrl.searchParams.set('state', state)
                }

                window.location.href = callbackUrl.toString()
                return
            }

            // Normal login flow (no marker, no extension)
            router.navigate({ to: returnToPath || '/' })
        },
        [router, returnToPath]
    )

    // Handle OAuth redirect result when user returns from redirect-based sign-in (Google or Apple)
    // Uses unified handler to avoid race condition where separate handlers would consume each other's credentials
    useEffect(() => {
        // Skip redirect result handling on native platforms - native auth is used instead
        // getRedirectResult() throws auth/argument-error on Capacitor iOS/Android
        if (!isWebPlatform()) {
            return
        }

        let isMounted = true

        const handleRedirectResult = async () => {
            try {
                const { credential, providerId, returnToPath: storedReturnToPath } =
                    await getOAuthRedirectResult()

                if (!credential || !providerId || !isMounted) {
                    return
                }

                // Set appropriate loading state based on provider
                const isGoogle = providerId === 'google.com'
                if (isGoogle) {
                    setIsGoogleSignInLoading(true)
                } else {
                    setIsAppleSignInLoading(true)
                }
                setDisplayError(null)

                const idToken = await credential.user.getIdToken()

                if (!idToken) {
                    throw new Error(
                        `Could not retrieve ID token from ${isGoogle ? 'Google' : 'Apple'} Sign-In.`
                    )
                }

                // Route to appropriate callback based on provider
                const callbackResponse = isGoogle
                    ? await googleAuthCallback({ idToken })
                    : await appleAuthCallback({ idToken })

                if (!isMounted) {
                    return
                }

                if (callbackResponse.status === 'logged_in') {
                    await handleSuccessfulAuth(idToken)
                } else if (callbackResponse.status === 'pending_registration') {
                    setRegistrationData({
                        authMethod: isGoogle ? 'google' : 'apple',
                        registrationToken: idToken,
                        email: credential.user.email || undefined,
                        fullName: credential.user.displayName || undefined,
                        avatarUrl: credential.user.photoURL || undefined,
                    })

                    // Navigate directly to username step (skip /register redirect)
                    router.navigate({
                        to: '/register/username',
                        search: {
                            returnToPath: storedReturnToPath || returnToPath,
                            error: undefined,
                        },
                    })
                } else {
                    throw new Error(
                        `Unexpected status from ${isGoogle ? 'Google' : 'Apple'} auth callback: ${callbackResponse.status}`
                    )
                }
            } catch (error: any) {
                if (!isMounted) {
                    return
                }
                console.error('OAuth redirect result handling failed:', error)
                // In redirect flow, user cancellation is handled by credential being null (early return above)
                // Any error here is a real error that should be reported
                captureException(error, {
                    authMethod: 'oauth-redirect',
                    returnToPath,
                    source: 'redirect',
                })
                setDisplayError(getErrorMessage(error as Error))
            } finally {
                if (isMounted) {
                    setIsGoogleSignInLoading(false)
                    setIsAppleSignInLoading(false)
                }
            }
        }

        handleRedirectResult()

        return () => {
            isMounted = false
        }
    }, [googleAuthCallback, appleAuthCallback, router, returnToPath, setRegistrationData, handleSuccessfulAuth, getErrorMessage])

    const handleBack = () => {
        router.navigate({ to: '/onboarding' })
    }

    const handleRegister = () => {
        router.navigate({ to: '/register', search: { returnToPath, error: undefined } })
    }

    const handleForgotPassword = () => {
        router.navigate({ to: '/forgot-password' })
    }

    const handleGoogleSignIn = async () => {
        setDisplayError(null)
        resetGoogleCallback()
        setIsGoogleSignInLoading(true)

        try {
            const userCredential = await signInWithGoogle(returnToPath)

            // If null, user was redirected (in-app browser flow)
            // The redirect result will be handled by the useEffect hook
            if (!userCredential) {
                return
            }

            const idToken = await userCredential.user.getIdToken()

            if (!idToken) {
                throw new Error(
                    'Could not retrieve ID token from Google Sign-In.'
                )
            }

            const callbackResponse = await googleAuthCallback({ idToken })

            if (callbackResponse.status === 'logged_in') {
                await handleSuccessfulAuth(idToken)
            } else if (callbackResponse.status === 'pending_registration') {
                setRegistrationData({
                    authMethod: 'google',
                    registrationToken: idToken,
                    email: userCredential.user.email || undefined,
                    fullName: userCredential.user.displayName || undefined,
                    avatarUrl: userCredential.user.photoURL || undefined,
                })

                // Navigate directly to username step (skip /register redirect)
                router.navigate({ to: '/register/username', search: { returnToPath, error: undefined } })
            } else {
                throw new Error(
                    `Unexpected status from Google auth callback: ${callbackResponse.status}`
                )
            }
        } catch (error: any) {
            console.error('Google sign in or callback failed:', error)
            if (error.code !== 'auth/popup-closed-by-user') {
                captureException(error, { authMethod: 'google', returnToPath })
                setDisplayError(getErrorMessage(error as Error))
            }
        } finally {
            setIsGoogleSignInLoading(false)
        }
    }

    const handleAppleSignIn = async () => {
        setDisplayError(null)
        resetAppleCallback()
        setIsAppleSignInLoading(true)

        try {
            const userCredential = await signInWithApple(returnToPath)

            // If null, user cancelled or was redirected (redirect flow on web)
            // On Capacitor, this means user cancelled - the finally block will reset loading
            if (!userCredential) {
                return
            }

            const idToken = await userCredential.user.getIdToken()

            if (!idToken) {
                throw new Error(
                    'Could not retrieve ID token from Apple Sign-In.'
                )
            }

            const callbackResponse = await appleAuthCallback({ idToken })

            if (callbackResponse.status === 'logged_in') {
                await handleSuccessfulAuth(idToken)
            } else if (callbackResponse.status === 'pending_registration') {
                setRegistrationData({
                    authMethod: 'apple',
                    registrationToken: idToken,
                    email: userCredential.user.email || undefined,
                    fullName: userCredential.user.displayName || undefined,
                    avatarUrl: userCredential.user.photoURL || undefined,
                })

                router.navigate({ to: '/register/username', search: { returnToPath, error: undefined } })
            } else {
                throw new Error(
                    `Unexpected status from Apple auth callback: ${callbackResponse.status}`
                )
            }
        } catch (error: any) {
            console.error('Apple sign in or callback failed:', error)
            // Don't show error for user cancellation
            const isCancelled =
                error.code === 'auth/popup-closed-by-user' ||
                error.code === 1000 || // Apple cancellation error (number)
                error.code === 1001 || // Apple cancellation error (number)
                error.code === '1000' || // Apple cancellation error (string)
                error.code === '1001' || // Apple cancellation error (string)
                error.code === 'CANCELED' ||
                error.message?.toLowerCase().includes('cancel') ||
                error.message?.includes('AuthorizationError') || // Apple native error pattern
                error.message?.includes('error 1000') || // Apple error 1000 in message
                error.message?.includes('error 1001') // Apple error 1001 in message

            if (!isCancelled) {
                captureException(error, { authMethod: 'apple', returnToPath })
                setDisplayError(getErrorMessage(error as Error))
            }
        } finally {
            setIsAppleSignInLoading(false)
        }
    }

    const handleLogin = (data: LoginFormData) => {
        setDisplayError(null)
        resetLogin()

        login(
            {
                email: data.email,
                password: data.password,
            },
            {
                onSuccess: async () => {
                    // Get the user's ID token for extension or normal flow
                    try {
                        const { getAuth } = await import('firebase/auth')
                        const auth = getAuth()
                        const currentUser = auth.currentUser

                        if (currentUser) {
                            const idToken = await currentUser.getIdToken()
                            await handleSuccessfulAuth(idToken)
                        } else {
                            // Check for marker to handle cleanup even without token
                            const markerStr =
                                sessionStorage.getItem('authFlowMarker')
                            if (markerStr) {
                                try {
                                    const marker = JSON.parse(markerStr) as {
                                        historyLength: number
                                        startPath: string
                                    }
                                    const stepsBack =
                                        window.history.length -
                                        marker.historyLength
                                    if (stepsBack > 0) {
                                        window.history.go(-stepsBack)
                                        return
                                    }
                                } catch (e) {
                                    console.error('Marker cleanup failed:', e)
                                    sessionStorage.removeItem('authFlowMarker')
                                }
                            }
                            router.navigate({ to: returnToPath || '/' })
                        }
                    } catch (error) {
                        console.error('Failed to get token after login:', error)
                        // Check for marker in error case too
                        const markerStr =
                            sessionStorage.getItem('authFlowMarker')
                        if (markerStr) {
                            try {
                                const marker = JSON.parse(markerStr) as {
                                    historyLength: number
                                    startPath: string
                                }
                                const stepsBack =
                                    window.history.length - marker.historyLength
                                if (stepsBack > 0) {
                                    window.history.go(-stepsBack)
                                    return
                                }
                            } catch (e) {
                                console.error('Marker cleanup failed:', e)
                                sessionStorage.removeItem('authFlowMarker')
                            }
                        }
                        router.navigate({ to: returnToPath || '/' })
                    }
                },
                onError: (err) => {
                    console.error('Login failed:', err)
                    setDisplayError(getErrorMessage(err))
                },
            }
        )
    }

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex justify-between px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    // src={assetPath('images/rectangle-logo.png')}
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
                <GlossyButton
                    variant="ghost"
                    className="rounded-xl bg-white/25 px-4 py-2.5 backdrop-blur md:fixed md:right-2.5"
                    onClick={handleRegister}
                    disabled={isLoading}
                >
                    Sign up
                </GlossyButton>
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Log in
                        <br className="md:hidden" />
                        <span className="text-white-48"> to your account</span>
                    </h1>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <LoginForm
                    onSubmit={handleLogin}
                    onBack={handleBack}
                    onGoogleSignIn={handleGoogleSignIn}
                    onAppleSignIn={handleAppleSignIn}
                    isSubmitting={isLoginLoading}
                    isGoogleSubmitting={isGoogleCallbackLoading || isGoogleSignInLoading}
                    isAppleSubmitting={isAppleCallbackLoading || isAppleSignInLoading}
                    submitError={displayError}
                    onForgotPassword={handleForgotPassword}
                />
            </div>
        </section>
    )
}

const loginSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

interface LoginFormProps {
    onSubmit: (data: LoginFormData) => void
    onBack: () => void
    onGoogleSignIn: () => void
    onAppleSignIn: () => void
    isSubmitting: boolean
    isGoogleSubmitting: boolean
    isAppleSubmitting: boolean
    submitError?: string | null
    onForgotPassword: () => void
}

function LoginForm({
    onSubmit,
    onBack,
    onGoogleSignIn,
    onAppleSignIn,
    isSubmitting,
    isGoogleSubmitting,
    isAppleSubmitting,
    submitError,
    onForgotPassword,
}: Readonly<LoginFormProps>) {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: 'onSubmit',
    })

    return (
        <>
            <FadeInUp delay={0.5}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
                    <GlossyButton
                        variant="social"
                        className="mb-1 w-full gap-2 rounded-xl bg-white/48 py-3 text-sm font-normal backdrop-blur"
                        glassEffect={{
                            opacity: 'bg-white/20',
                            blur: 'backdrop-blur-md',
                        }}
                        type="button"
                        onClick={onGoogleSignIn}
                        disabled={isSubmitting || isGoogleSubmitting || isAppleSubmitting}
                    >
                        <GoogleIcon />
                        {isGoogleSubmitting
                            ? 'Connecting...'
                            : 'Continue with Google'}
                    </GlossyButton>

                    <GlossyButton
                        variant="social"
                        className="mb-1 w-full gap-2 rounded-xl bg-white/48 py-3 text-sm font-normal backdrop-blur"
                        glassEffect={{
                            opacity: 'bg-white/20',
                            blur: 'backdrop-blur-md',
                        }}
                        type="button"
                        onClick={onAppleSignIn}
                        disabled={isSubmitting || isGoogleSubmitting || isAppleSubmitting}
                    >
                        <AppleIcon />
                        {isAppleSubmitting
                            ? 'Connecting...'
                            : 'Continue with Apple'}
                    </GlossyButton>

                    <div className="space-y-1">
                        <div>
                            <GlossyInput
                                {...register('email')}
                                placeholder="Email"
                                className={`${errors.email ? 'border-red-500' : ''}`}
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                                focusedBorderColor={
                                    errors.email
                                        ? 'border-red-500'
                                        : 'border-white'
                                }
                                unfocusedBorderColor={
                                    errors.email
                                        ? 'border-red-500/70'
                                        : 'border-white/30'
                                }
                            />
                            {errors.email && (
                                <FormErrorMessage
                                    message={errors.email.message || ''}
                                />
                            )}
                        </div>

                        <div>
                            <GlossyInput
                                {...register('password')}
                                type="password"
                                className={`${errors.password ? 'border-red-500' : ''}`}
                                placeholder="Password"
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                                focusedBorderColor={
                                    errors.password
                                        ? 'border-red-500'
                                        : 'border-white'
                                }
                                unfocusedBorderColor={
                                    errors.password
                                        ? 'border-red-500/70'
                                        : 'border-white/30'
                                }
                            />
                            {errors.password && (
                                <FormErrorMessage
                                    message={errors.password.message || ''}
                                />
                            )}
                        </div>

                        {/* Show API error */}
                        {submitError && (
                            <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-500/20 p-2 backdrop-blur">
                                <AlertCircle
                                    className="text-red-500"
                                    size={16}
                                />
                                <p className="text-xs text-red-400">
                                    {submitError}
                                </p>
                            </div>
                        )}

                        <div className="text-center">
                            <button
                                type="button"
                                className="mt-[24px] text-sm font-semibold text-white underline"
                                onClick={onForgotPassword}
                            >
                                Forgot password?
                            </button>
                        </div>
                    </div>
                </form>
            </FadeInUp>

            <FadeInUp delay={0.7} className="mt-auto mb-6">
                <div className="flex items-center gap-2 pt-6">
                    <GlossyButton
                        variant="ghost"
                        className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                        type="button"
                        onClick={onBack}
                        disabled={isSubmitting || isGoogleSubmitting || isAppleSubmitting}
                    >
                        <ArrowLeft strokeWidth={1} className="mr-1 h-5 w-5" />
                        Back
                    </GlossyButton>
                    <GlossyButton
                        className="w-full rounded-xl border-none py-[13px] text-sm"
                        type="submit"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || isGoogleSubmitting || isAppleSubmitting}
                    >
                        {isSubmitting ? 'Logging in...' : 'Log in'}
                    </GlossyButton>
                </div>
            </FadeInUp>
        </>
    )
}
